import { lazy, Suspense, useEffect, type ComponentType, type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { trackMarketplaceEvent } from '../lib/track';
import { HomePage } from '../pages/home';
import {
  AccountShellSkeleton,
  CenteredShellSkeleton,
  FlowSkeleton,
  SalonShellSkeleton,
  SearchShellSkeleton,
} from '../components/page-skeletons';

// M7 route-level code splitting: only Home (the landing page) ships in the main
// bundle. Every other page is its own chunk, shown behind a skeleton of the
// same layout while it loads.
// A tab opened before a deploy can ask for a chunk that's since been pruned
// (the deploy keeps old hashed files for 30 days). Reload once to pick up the
// new index.html instead of leaving a blank page; a second failure throws.
const RELOAD_FLAG = 'sl_chunk_reload';
function reloadOnceOnChunkError(err: unknown): never {
  let reloaded = false;
  try {
    reloaded = sessionStorage.getItem(RELOAD_FLAG) === '1';
    if (!reloaded) sessionStorage.setItem(RELOAD_FLAG, '1');
  } catch {
    reloaded = true; // no storage → never risk a reload loop
  }
  if (!reloaded) window.location.reload();
  throw err;
}

const named = <K extends string>(load: () => Promise<Record<K, ComponentType>>, name: K) =>
  lazy(() =>
    load().then((m) => {
      try {
        sessionStorage.removeItem(RELOAD_FLAG);
      } catch {
        /* storage unavailable */
      }
      return { default: m[name] };
    }, reloadOnceOnChunkError),
  );

const SearchPage = named(() => import('../pages/search'), 'SearchPage');
const SalonPage = named(() => import('../pages/salon'), 'SalonPage');
const LoginPage = named(() => import('../pages/login'), 'LoginPage');
const MyBookingsPage = named(() => import('../pages/my-bookings'), 'MyBookingsPage');
const ProfilePage = named(() => import('../pages/profile'), 'ProfilePage');
const BookingCompletePage = named(() => import('../pages/booking-complete'), 'BookingCompletePage');
const NotFoundPage = named(() => import('../pages/not-found'), 'NotFoundPage');
const BookingPage = named(() => import('../booking/booking-page'), 'BookingPage');
const BookedPage = named(() => import('../booking/booked-page'), 'BookedPage');
const ReschedulePage = named(() => import('../account/reschedule-page'), 'ReschedulePage');

function Lazy({ fallback, children }: { fallback: ReactNode; children: ReactNode }) {
  return <Suspense fallback={fallback}>{children}</Suspense>;
}

/** One PAGE_VIEW analytics ping per client-side navigation (operator console). */
function PageViewTracker() {
  const { pathname } = useLocation();
  useEffect(() => {
    trackMarketplaceEvent('PAGE_VIEW');
  }, [pathname]);
  return null;
}

/** `/salon/:subdomain/book` without a step starts at the service step (query kept). */
function BookingIndexRedirect() {
  const { subdomain } = useParams();
  const { search } = useLocation();
  return <Navigate replace to={`/salon/${encodeURIComponent(subdomain ?? '')}/book/service${search}`} />;
}

export function App() {
  return (
    <>
      <PageViewTracker />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/search" element={<Lazy fallback={<SearchShellSkeleton />}><SearchPage /></Lazy>} />
        <Route path="/salon/:subdomain" element={<Lazy fallback={<SalonShellSkeleton />}><SalonPage /></Lazy>} />
        {/* Booking flow (M4): one route for all steps keeps the flow + hold mounted. */}
        <Route path="/salon/:subdomain/book" element={<BookingIndexRedirect />} />
        <Route path="/salon/:subdomain/book/:step" element={<Lazy fallback={<FlowSkeleton />}><BookingPage /></Lazy>} />
        <Route path="/salon/:subdomain/booked/:ref" element={<Lazy fallback={<CenteredShellSkeleton />}><BookedPage /></Lazy>} />
        <Route path="/login" element={<Lazy fallback={<CenteredShellSkeleton variant="auth" />}><LoginPage /></Lazy>} />
      {/* Magic-link emails point here (MarketplaceAuthService builds
          `${base}/magic-link?token=`). LoginPage already consumes `?token`,
          so alias it rather than rename the minted URL — links already sent
          to inboxes keep working. */}
        <Route path="/magic-link" element={<Lazy fallback={<CenteredShellSkeleton />}><LoginPage /></Lazy>} />
        <Route path="/me/bookings" element={<Lazy fallback={<AccountShellSkeleton />}><MyBookingsPage /></Lazy>} />
        <Route path="/me/bookings/:id/reschedule" element={<Lazy fallback={<AccountShellSkeleton reschedule />}><ReschedulePage /></Lazy>} />
        <Route path="/me/profile" element={<Lazy fallback={<AccountShellSkeleton />}><ProfilePage /></Lazy>} />
      {/* Stripe async-payment return URL (Pix/Bancontact). Reads the
          `payment_intent` query param and polls the booking-intent status. */}
        <Route path="/booking/complete" element={<Lazy fallback={<CenteredShellSkeleton />}><BookingCompletePage /></Lazy>} />
        <Route path="*" element={<Lazy fallback={<CenteredShellSkeleton />}><NotFoundPage /></Lazy>} />
      </Routes>
    </>
  );
}

export default App;
